import buttonCRUDActions, {
  useButtonCRUDActions,
} from '@mm/state/actions/ButtonCRUDActions';
import widgetCRUDActions from '@mm/state/actions/WidgetCRUDActions';
import ButtonModel, {
  ButtonType,
  PageButtonConfig,
} from '@mm/state/model/ButtonModel';
import WidgetModel, {
  QuickQuestionWidgetType,
  WidgetTypes,
} from '@mm/state/model/WidgetModel';
import { StoreState } from '@mm/state/store-interfaces';
import { cloneDeep } from 'lodash';
import { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { TYPES } from '../../../editor/features/build/components/widgets/constants/quickQuestion';
import { makeButton } from '../../../editor/features/build/requestModels/widgets/buttonStackWidget/Button';
import QuickQuestionWidget from '../../../editor/features/build/requestModels/widgets/QuickQuestionWidget';
import { PathGraphContext } from '../../widgets/common/PathGraph/PathGraph';
import ObjectUtils from '../utils/object-utils';
import { getBotAttributes } from './use-attribute-handlers';
import { useButtonHandlers } from './use-button-handlers';
import { useCommonWidgetHandlers } from './use-common-widget-handlers';

export const getQQUserResponseType = (widget: WidgetModel) => {
  if (widget.buttons && widget.buttons.length === 1) {
    const firstButton = widget.buttons[0];

    switch (firstButton.type) {
      case ButtonType.OPT_IN:
        return TYPES.MULTIPLE;
      case ButtonType.PHONE_NUMBER:
        return TYPES.PHONE;
      case ButtonType.EMAIL:
        return TYPES.EMAIL;
      case ButtonType.POSTBACK:
        return TYPES.MULTIPLE;
      case ButtonType.PAGE:
        return TYPES.MULTIPLE;

      // Not sure if this case will ever be met but was in code before also
      default:
        return '';
    }
  }
  if (widget.config.allowFreeTextResponse) {
    return TYPES.TEXT;
  }

  return TYPES.MULTIPLE;
};

const useOnChangeResponseType = () => {
  const dispatch = useDispatch();
  const attributes = useSelector((state: StoreState) =>
    getBotAttributes(state.attribute.entities, false),
  );

  const removeAllButtons = (widget: WidgetModel) => {
    return {
      ...widget,
      buttons: [],
      config: {
        ...widget.config,
        allowFreeTextResponse: true,
      },
    };
  };

  const addPhoneButton = async (widget: WidgetModel) => {
    const maxSortOrder = Math.max(...widget.buttons.map(b => b.sortOrder), 0);
    const newButton = makeButton({
      type: ButtonType.PHONE_NUMBER,
      name: '',
      sortOrder: maxSortOrder + 1,
      id: null,
    }) as ButtonModel;

    return dispatch(buttonCRUDActions.create(widget.id, newButton));
  };

  const addEmailButton = async (widget: WidgetModel) => {
    const maxSortOrder = Math.max(...widget.buttons.map(b => b.sortOrder), 0);
    const newButton = makeButton({
      type: ButtonType.EMAIL,
      name: '',
      sortOrder: maxSortOrder + 1,
      id: null,
    }) as ButtonModel;

    return dispatch(buttonCRUDActions.create(widget.id, newButton));
  };

  const setWidgetValidationRule = (
    widget: WidgetModel,
    validationRule: string | null,
  ): WidgetModel => {
    return {
      ...widget,
      config: {
        ...widget.config,
        validationRule,
      },
    };
  };

  const onChangeInputType = (
    widget: WidgetModel,
    value: boolean,
    keepButtons?: boolean,
  ) => {
    const { config } = widget;

    return {
      ...widget,
      buttons: value && !keepButtons ? [] : widget.buttons,
      config: {
        ...config,
        allowFreeTextResponse: value,
      },
    };
  };

  return async (widget: WidgetModel, type: string) => {
    let widgetCopy = { ...widget };
    const currentIsPhoneOrEmail = ['email', 'phone'].some(
      t => widgetCopy.config.validationRule === t,
    );
    const futureIsPhoneOrEmail = [TYPES.EMAIL, TYPES.PHONE].some(
      t => type === t,
    );
    const skipAttributeReset = !currentIsPhoneOrEmail && !futureIsPhoneOrEmail;
    if (!skipAttributeReset && widgetCopy.type !== type) {
      widgetCopy = {
        ...widgetCopy,
        config: {
          ...widgetCopy.config,
          // @ts-ignore Note: need to overridge to null to delete the attribute
          variableId: null,
        },
      };
    }
    switch (type) {
      case TYPES.MULTIPLE: {
        let changedWidget = setWidgetValidationRule(widgetCopy, null);
        changedWidget = removeAllButtons(changedWidget);
        changedWidget = onChangeInputType(changedWidget, false);
        await dispatch(widgetCRUDActions.update(changedWidget));
        break;
      }

      case TYPES.TEXT: {
        let changedWidget = setWidgetValidationRule(widgetCopy, null);
        changedWidget = removeAllButtons(changedWidget);
        changedWidget = onChangeInputType(changedWidget, true);

        await dispatch(widgetCRUDActions.update(changedWidget));
        break;
      }

      case TYPES.PHONE: {
        let changedWidget = setWidgetValidationRule(widgetCopy, 'phone');
        changedWidget = removeAllButtons(changedWidget);

        const phoneAttribute = attributes.find(a => a.name === 'PHONE');
        if (phoneAttribute) {
          changedWidget = {
            ...changedWidget,
            config: {
              ...changedWidget.config,
              variableId: phoneAttribute.id,
              variableType: 'Variable',
            },
          };
        }
        await dispatch(widgetCRUDActions.update(changedWidget));

        await addPhoneButton(changedWidget);
        break;
      }

      case TYPES.EMAIL: {
        let changedWidget = setWidgetValidationRule(widgetCopy, 'email');
        changedWidget = removeAllButtons(changedWidget);

        const emailAttribute = attributes.find(a => a.name === 'EMAIL');
        if (emailAttribute) {
          changedWidget = {
            ...changedWidget,
            config: {
              ...changedWidget.config,
              variableId: emailAttribute.id,
              variableType: 'Variable',
            },
          };
        }
        await dispatch(widgetCRUDActions.update(changedWidget));

        await addEmailButton(changedWidget);
        break;
      }

      default: {
        return null;
      }
    }
  };
};

export const useQQWidgetHandlers = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();
  const onChangeResponseType = useOnChangeResponseType();
  const commonWidgetHandlers = useCommonWidgetHandlers();
  const buttonHandlers = useButtonHandlers();
  const { addWidgetButtonsToGraph, removeNodeAndSuccessors } =
    useContext(PathGraphContext);
  const buttonsCRUDs = useButtonCRUDActions();

  const attributes = useSelector((state: StoreState) =>
    getBotAttributes(state.attribute.entities, false),
  );

  const updateAssociatedDialogue = (
    widget: WidgetModel,
    button: ButtonModel,
    pageId: number | undefined,
  ) => {
    const updatedButton = cloneDeep(button);

    if (pageId) {
      (updatedButton.config as PageButtonConfig).pageId = pageId;
      if (updatedButton.type !== ButtonType.OPT_IN) {
        updatedButton.type = ButtonType.PAGE;
      }
      ObjectUtils.removeProperty('payload', updatedButton);

      removeNodeAndSuccessors?.({
        id: button.id,
        type: 'button',
        actionType: 'deleteSuccessors',
      });

      addWidgetButtonsToGraph?.(widget.id, [updatedButton]);
    } else {
      if (updatedButton.type !== ButtonType.OPT_IN) {
        updatedButton.type = ButtonType.POSTBACK;
      }
      ObjectUtils.removeProperty('pageId', updatedButton);
    }

    return dispatch(buttonCRUDActions.update(widget.id, updatedButton));
  };

  const addNewQuestion = async (
    type: QuickQuestionWidgetType,
    dialogueId: number,
    sortOrder: number,
    body?: string,
  ) => {
    const newWidget = QuickQuestionWidget.make(type, dialogueId, sortOrder);
    return commonWidgetHandlers.create(
      body
        ? { ...newWidget, config: { ...newWidget.config, body } }
        : newWidget,
      dialogueId,
      sortOrder,
    );
  };
  const addBaseQuestion = (
    dialogueId: number,
    sortOrder: number,
    isSmsRelated?: boolean,
    body?: string,
  ) => addNewQuestion(WidgetTypes.QUICK_QUESTION, dialogueId, sortOrder, body);

  const addEmailQuestion = async (dialogueId: number, sortOrder: number) => {
    const newWidget = QuickQuestionWidget.make(
      WidgetTypes.QUICK_QUESTION,
      dialogueId,
      sortOrder,
    );
    try {
      const emailAttribute = attributes.find(a => a.name === 'EMAIL');
      const savedWidget = await commonWidgetHandlers.create(
        {
          ...newWidget,
          config: {
            ...newWidget.config,

            allowFreeTextResponse: true,
            validationRule: 'email',
            ...(emailAttribute
              ? { variableId: emailAttribute.id, variableType: 'Variable' }
              : {}),
          },
        },
        dialogueId,
        sortOrder,
      );

      if (savedWidget.id) {
        const maxSortOrder = Math.max(
          ...savedWidget.buttons.map(b => b.sortOrder),
          0,
        );
        const newButton = makeButton({
          type: ButtonType.EMAIL,
          name: '',
          sortOrder: maxSortOrder + 1,
          id: null,
        }) as ButtonModel;

        await buttonsCRUDs.create(savedWidget.id, newButton);
        return savedWidget;
      }
    } catch (e) {}
  };

  const addPhoneQuestion = async (dialogueId: number, sortOrder: number) => {
    const newWidget = QuickQuestionWidget.make(
      WidgetTypes.QUICK_QUESTION,
      dialogueId,
      sortOrder,
    );
    try {
      const phoneAttribute = attributes.find(a => a.name === 'PHONE');
      const savedWidget = await commonWidgetHandlers.create(
        {
          ...newWidget,
          config: {
            ...newWidget.config,
            allowFreeTextResponse: true,
            validationRule: 'phone',
            ...(phoneAttribute
              ? { variableId: phoneAttribute.id, variableType: 'Variable' }
              : {}),
          },
        },
        dialogueId,
        sortOrder,
      );

      if (savedWidget.id) {
        const maxSortOrder = Math.max(
          ...savedWidget.buttons.map(b => b.sortOrder),
          0,
        );
        const newButton = makeButton({
          type: ButtonType.PHONE_NUMBER,
          name: '',
          sortOrder: maxSortOrder + 1,
          id: null,
        }) as ButtonModel;

        await buttonsCRUDs.create(savedWidget.id, newButton);
        return savedWidget;
      }
    } catch (e) {}
  };

  const addInfoQuestion = async (dialogueId: number, sortOrder: number) => {
    const newWidget = QuickQuestionWidget.make(
      WidgetTypes.QUICK_QUESTION,
      dialogueId,
      sortOrder,
    );
    try {
      const savedWidget = await commonWidgetHandlers.create(
        {
          ...newWidget,
          config: {
            ...newWidget.config,
            allowFreeTextResponse: true,
            validationRule: null,
          },
        },
        dialogueId,
        sortOrder,
      );

      return savedWidget;
    } catch (e) {}
  };

  const addSmsOptInQuestion = (dialogueId: number, sortOrder: number) =>
    addNewQuestion(WidgetTypes.SMS_OPT_IN_V2, dialogueId, sortOrder);

  const updateProcessedByTriggers = (
    widget: WidgetModel,
    processedByTriggers: boolean,
  ) => {
    const updatedWidget = {
      ...widget,
      config: {
        ...widget.config,
        processedByTriggers,
      },
    };

    dispatch(widgetCRUDActions.update(updatedWidget));
  };

  const updateShouldSkip = (widget: WidgetModel, skipSendWidget: boolean) => {
    const updatedWidget = {
      ...widget,
      config: {
        ...widget.config,
        skipSendWidget,
      },
    };

    dispatch(widgetCRUDActions.update(updatedWidget));
  };

  const updateButtons = async (widget: WidgetModel, buttons: ButtonModel[]) => {
    const updatedWidget = {
      ...widget,
      buttons,
    };

    await dispatch(widgetCRUDActions.update(updatedWidget));
  };

  const clearAttribute = (widget: WidgetModel) => {
    const updatedWidget = {
      ...widget,
      config: {
        ...widget.config,
        variableId: null,
      },
    };

    dispatch(widgetCRUDActions.update(updatedWidget));
  };

  const setDisableComposerInput = async (
    widget: WidgetModel,
    disable: boolean,
  ) => {
    return commonWidgetHandlers.updateField(
      widget,
      'config.blocksInput',
      disable,
    );
  };

  return {
    delete: commonWidgetHandlers.delete,
    updateQuestionText: commonWidgetHandlers.updateBodyText,
    deleteButton: buttonHandlers.delete,
    createPathButtonBundle: buttonHandlers.createPathBundle,
    deletePathButtonBundle: buttonHandlers.deletePathBundle,
    updateButtonText: buttonHandlers.updateText,
    addPostBackButton: buttonHandlers.addPostBackButton,
    addImageToPostBackButton: buttonHandlers.addImageToPostBackButton,
    removeImageFromPostBackButton: buttonHandlers.removeImageFromPostBackButton,
    updateSmsResponse: buttonHandlers.updateSmsResponse,
    setDisableComposerInput,
    onChangeResponseType,
    addBaseQuestion,
    addEmailQuestion,
    addPhoneQuestion,
    addInfoQuestion,
    addSmsOptInQuestion,
    updateAssociatedDialogue,
    updateProcessedByTriggers,
    updateShouldSkip,
    updateButtons,
    clearAttribute,
  };
};
